/**
 * Big bodies in a Monaco editor (owner, 20.10.2026: a 9.5 MB, ~500k-line XML
 * pasted into Body → Raw froze the app).
 *
 * What VS Code does for a large file, done here for a large body: past a size
 * the editor keeps editing and scrolling smooth by turning off what scales with
 * the whole text — wrapping, folding, sticky scroll, links, word-based
 * suggestions, occurrence highlights — and says so in a small notice. Syntax
 * highlighting stays: Monaco colours only what is on screen and tokenizes the
 * rest in idle time, and Postman keeps it too (owner, 20.10.2026: "postman
 * handles it with ease"). The text itself is never touched.
 */

/** A body is "large" from 2 MB or 50 000 lines on. */
export const LARGE_TEXT_CHARS = 2 * 1024 * 1024;
export const LARGE_TEXT_LINES = 50_000;

/** 0 normal, 1 large. */
export function textLevel(length, lineCount) {
  return length >= LARGE_TEXT_CHARS || lineCount >= LARGE_TEXT_LINES ? 1 : 0;
}

/** Same, for a string — counts lines only as far as it has to. */
export function textLevelOf(text) {
  const s = typeof text === 'string' ? text : '';
  if (s.length >= LARGE_TEXT_CHARS) return 1;
  if (s.length < LARGE_TEXT_LINES - 1) return 0; // fewer newlines than it takes
  let lines = 1;
  for (let i = s.indexOf('\n'); i !== -1 && lines < LARGE_TEXT_LINES; i = s.indexOf('\n', i + 1)) lines += 1;
  return textLevel(s.length, lines);
}

/** Whether a text of `length` characters and `lineCount` lines gets the large-text editor. */
export function isLargeSize(length, lineCount) {
  return textLevel(length, lineCount) >= 1;
}

/** Same, for a string. */
export function isLargeText(text) {
  return textLevelOf(text) >= 1;
}

/** Monaco options merged over the normal ones while the text is large. */
export const LARGE_TEXT_EDITOR_OPTIONS = Object.freeze({
  wordWrap: 'off',
  folding: false,
  stickyScroll: { enabled: false },
  links: false,
  wordBasedSuggestions: 'off',
  quickSuggestions: false,
  occurrencesHighlight: 'off',
  selectionHighlight: false,
  matchBrackets: 'never',
  bracketPairColorization: { enabled: false },
  guides: { indentation: false, bracketPairs: false },
  renderLineHighlight: 'none',
});

/** "9.5 MB" — the size the notice names. */
export function formatTextSize(chars) {
  const mb = chars / (1024 * 1024);
  if (mb >= 1) return `${mb >= 10 ? Math.round(mb) : mb.toFixed(1)} MB`;
  return `${Math.max(1, Math.round(chars / 1024))} KB`;
}

/**
 * The line ending a pasted text brings with it: 'crlf', 'lf', or null when it
 * has no line break at all. A Monaco model holds ONE line ending and converts
 * everything inserted into it — so a Windows editor (CRLF by default) turned
 * every \n of a pasted LF file into \r\n, and the body that was sent was not
 * the body that was pasted.
 */
export function lineEndingOf(text) {
  if (typeof text !== 'string') return null;
  const crlf = text.indexOf('\r\n');
  if (crlf !== -1) return 'crlf';
  return text.indexOf('\n') !== -1 ? 'lf' : null;
}

/**
 * The line ending a paste that replaces the WHOLE text gives the editor.
 * On Windows the clipboard holds CRLF whatever was copied — Chromium, and so
 * WebView2, writes every \n as \r\n — so its ending says nothing about the
 * source: take LF, which a 9.5 MB XML export has. Elsewhere the clipboard is
 * faithful and the paste keeps its own.
 */
export function pastedLineEnding(text, clipboardWritesCrlf) {
  const own = lineEndingOf(text);
  return own && clipboardWritesCrlf ? 'lf' : own;
}

/**
 * `{{name}}` tokens in `text`, which starts at `baseOffset` in the model.
 * Returns model offsets, so a caller scanning only the visible part of a large
 * text gets positions it can hand straight to `model.getPositionAt`.
 */
export function findVariableTokens(text, baseOffset = 0) {
  const out = [];
  const re = /{{\s*([^}]+)\s*}}/g;
  let m;
  while ((m = re.exec(text)) !== null) {
    out.push({ start: baseOffset + m.index, end: baseOffset + m.index + m[0].length, name: m[1].trim() });
  }
  return out;
}
